/**
 * NodeMaterialAsset — base mixin for serializable WebGPU TSL node-material asset classes.
 *
 * ## Design pattern
 * Each concrete subclass extends a `three/webgpu` node material *and* mixes in this class.
 * Authored fields (color, roughness, speed, etc.) are declared as normal TypeScript class
 * properties and decorated with `@property()` from the engine serialization decorator.
 * A `rebuild()` method translates those authored fields into the TSL node graph
 * (`colorNode`, `positionNode`, `emissiveNode`, ...) every time the object is constructed
 * or an authored field changes via the property inspector.
 *
 * This is the single-class variant of the MatcapShaderMaterial + GenesysMatcapNodeMaterial
 * dual-backend pattern. Because node materials already run on the WebGPU renderer, no
 * separate runtime counterpart is needed — the asset class IS the runtime material.
 *
 * ## How to create a new node-material asset
 * ```ts
 * import * as THREE from 'three';
 * import { MeshBasicNodeMaterial } from 'three/webgpu';
 * import { EngineClass } from '../../systems/ClassRegistry.js';
 * import { property } from '../../utils/serialization/decorator.js';
 * import { NodeMaterialAsset } from './NodeMaterialAsset.js';
 *
 * @EngineClass('GnsxMyCustomNodeMaterial', { isNodeMaterialAsset: true, nodeMaterialDisplayName: 'My Custom' })
 * export class GnsxMyCustomNodeMaterial extends NodeMaterialAsset(MeshBasicNodeMaterial) {
 *   @property({ type: 'color', description: 'Tint color' })
 *   color = new THREE.Color('#44aaff');
 *
 *   constructor() {
 *     super();
 *     this.rebuild();
 *   }
 *
 *   override rebuild(): void {
 *     this.colorNode = null; // default materialColor = color × map (matches WebGL)
 *   }
 * }
 * ```
 */

import * as THREE from 'three';

import { UrlTexture } from '../../../render/Texture.js';
import { getPropertyMetadata, property } from '../../../utils/serialization/decorator.js';
import { ISerializableObjectTag } from '../../../utils/serialization/serializable.js';

import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';
import type { ColorSpace, Texture, Wrapping } from 'three';


type AnyConstructor = new (...args: any[]) => object;
const VALID_SIDES = new Set<number>([
  THREE.FrontSide,
  THREE.BackSide,
  THREE.DoubleSide,
]);
const VALID_BLENDINGS = new Set<number>([
  THREE.NoBlending,
  THREE.NormalBlending,
  THREE.AdditiveBlending,
  THREE.SubtractiveBlending,
  THREE.MultiplyBlending,
  THREE.CustomBlending,
]);

/**
 * Mixin factory that adds NodeMaterialAsset behaviour to any node-material base class.
 *
 * Usage: `class MyMaterial extends NodeMaterialAsset(MeshBasicNodeMaterial) { ... }`
 */
export function NodeMaterialAsset<TBase extends AnyConstructor>(Base: TBase) {
  abstract class NodeMaterialAssetMixin extends Base {
    /** Marker used by ClassRegistry.isNodeMaterialAssetClass for isinstance-style checks. */
    static readonly isNodeMaterialAsset = true as const;
    readonly _urlTextures = new Map<string, UrlTexture>();

    constructor(...args: any[]) {
      super(...args);
      Object.defineProperty(this, ISerializableObjectTag, {
        value: true,
        enumerable: false,
        // configurable/writable true so legacy game subclasses that still declare
        // `readonly [ENGINE.ISerializableObjectTag] = true` can redefine without throwing.
        configurable: true,
        writable: true,
      });
    }

    @property({ type: 'boolean', description: 'Whether this material is visible' })
      visible = true;

    @property({ type: 'boolean', description: 'Whether this material is transparent' })
      transparent = false;

    @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Material opacity' })
      opacity = 1.0;

    @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Alpha test threshold' })
      alphaTest = 0.0;

    @property({ type: 'boolean', description: 'Whether to render transparent double-sided in a single pass' })
      forceSinglePass = false;

    @property({ type: 'boolean', description: 'Whether depth testing is enabled' })
      depthTest = true;

    @property({ type: 'boolean', description: 'Whether depth writing is enabled' })
      depthWrite = true;

    @property({ type: 'number', description: 'Face side mode (`THREE.FrontSide`, `BackSide`, `DoubleSide`)' })
      side: THREE.Side = THREE.FrontSide;

    @property({ type: 'number', description: 'Blend mode (`THREE.NormalBlending`, etc.)' })
      blending: THREE.Blending = THREE.NormalBlending;

    /**
     * Rebuilds the TSL node graph from current authored fields.
     * Called from the constructor and whenever a property inspector change is committed.
     */
    abstract rebuild(): void;

    /**
     * Applies common authored material fields defensively.
     * Keep all values in safe ranges and avoid unsupported runtime crashes.
     */
    public applyCommonMaterialState(): void {
      this.opacity = this.clampFiniteNumber(this.opacity, 0, 1, 1.0);
      this.alphaTest = this.clampFiniteNumber(this.alphaTest, 0, 1, 0.0);
      if (!VALID_SIDES.has(this.side)) {
        this.side = THREE.FrontSide;
      }
      if (!VALID_BLENDINGS.has(this.blending)) {
        this.blending = THREE.NormalBlending;
      }
    }

    /** Normalizes unknown values into a finite clamped number with fallback defaults. */
    public clampFiniteNumber(value: unknown, min: number, max: number, fallback: number): number {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return fallback;
      }
      return THREE.MathUtils.clamp(value, min, max);
    }

    /** Normalizes unknown values into a finite number with floor + fallback. */
    public floorFiniteNumber(value: unknown, min: number, fallback: number): number {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return fallback;
      }
      return Math.max(min, value);
    }

    /** Treats malformed texture-path inputs as empty paths. */
    public coerceTexturePath(value: unknown): string {
      return typeof value === 'string' ? value.trim() : '';
    }

    /** Returns authored color when valid, otherwise a stable fallback color. */
    public coerceColor(value: unknown, fallback: THREE.ColorRepresentation): THREE.Color {
      if (value instanceof THREE.Color) {
        return value;
      }
      const safeColor = new THREE.Color();
      try {
        safeColor.set(value as THREE.ColorRepresentation);
        return safeColor;
      } catch {
        safeColor.set(fallback);
        return safeColor;
      }
    }

    /**
     * Synchronizes a TexturePath field to an actual material texture slot.
     * Uses UrlTexture so authored paths round-trip through serialization.
     */
    public syncTexturePath(
      textureProp: string,
      urlValue: string,
      colorSpace: ColorSpace,
      wrapS: Wrapping = THREE.RepeatWrapping,
      wrapT: Wrapping = THREE.RepeatWrapping
    ): void {
      const material = this as unknown as Record<string, unknown>;
      if (!(textureProp in material)) {
        return;
      }

      const trimmed = this.coerceTexturePath(urlValue);
      if (trimmed.length === 0) {
        material[textureProp] = null;
        return;
      }

      let texture = this._urlTextures.get(textureProp);
      if (!texture) {
        texture = new UrlTexture({ url: trimmed, wrapS, wrapT, colorSpace });
        this._urlTextures.set(textureProp, texture);
      } else if (texture.url !== trimmed) {
        texture.url = trimmed;
      }

      texture.wrapS = wrapS;
      texture.wrapT = wrapT;
      texture.colorSpace = colorSpace;
      texture.needsUpdate = true;
      material[textureProp] = texture as Texture;
    }

    /**
     * Serializes all `@property()`-decorated authored fields to the Dumper.
     * Call this from your class's `serialize(dumper)` method.
     */
    public serializeAuthoredFields(dumper: IDumper): void {
      const meta = getPropertyMetadata(this);
      for (const key of Object.keys(meta)) {
        dumper.dumpValue(key, (this as unknown as Record<string, unknown>)[key]);
      }
    }

    /**
     * Deserializes all `@property()`-decorated authored fields from the Loader.
     * Call this from your class's static `staticDeserialize` after constructing the instance.
     */
    public static loadAuthoredFields(instance: NodeMaterialAssetMixin, loader: ILoader): void {
      const meta = getPropertyMetadata(instance);
      for (const key of Object.keys(meta)) {
        const value = loader.loadValue(key);
        if (value !== undefined) {
          (instance as unknown as Record<string, unknown>)[key] = value;
        }
      }
    }
  }

  return NodeMaterialAssetMixin;
}
